import React from 'react';

import { Col } from '@opendoor/bricks-next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { getBuyDirectCheckoutLayout } from 'components/buyer/buyDirectCheckout/BuyDirectCheckoutLayout';
import { BricksNextLoader as Loader } from 'components/shared/BricksNextLoader';
import GlobalStyles from 'components/shared/GlobalStyles';

import { NextPageWithLayout } from '../_app';

/**
 * Dynamically import the checkout flow to code-split its heavy dependency tree
 * (XState, step components, sidebar, API clients) out of the main page bundle.
 * SSR is disabled because the flow requires authentication and is entirely client-driven.
 */
const BuyDirectCheckout = dynamic(
  () =>
    import('components/buyer/buyDirectCheckout/routes/BuyDirectCheckout').then(
      (mod) => mod.BuyDirectCheckout,
    ),
  {
    ssr: false,
    loading: () => (
      <Col alignItems="center" justifyContent="center" paddingVertical="$20">
        <Loader size="small" backgroundColor="$backgroundPrimary" />
      </Col>
    ),
  },
);

/**
 * Buy Direct checkout page
 */
const BuyDirectCheckoutPage: React.FC = () => {
  const router = useRouter();
  const { addressId: addressIdParam } = router.query;
  const addressId = typeof addressIdParam === 'string' ? addressIdParam : undefined;

  return (
    <>
      <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
      <Head>
        <title>Buy Direct Checkout | Opendoor</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <BuyDirectCheckout addressId={addressId} />
    </>
  );
};

const BuyDirectCheckoutPageWithLayout = BuyDirectCheckoutPage as NextPageWithLayout & {
  theme?: string;
};
BuyDirectCheckoutPageWithLayout.getLayout = getBuyDirectCheckoutLayout;
BuyDirectCheckoutPageWithLayout.theme = 'novo';

export default BuyDirectCheckoutPageWithLayout;
